import type { Unit } from "@/lib/types/unit";
import type { Motion } from "@/lib/types/motion";
import type { DamageCalculationResult } from "@/lib/damage-calculator/damage-calculator-utils";
import { TraitToggle } from "./TraitToggle";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface ActiveTraitsDisplayProps {
  sourceUnit?: Unit;
  targetUnit?: Unit;
  distance: number;
  motion: Motion;
  sourceTraitOverrides: Map<string, boolean | undefined>;
  targetTraitOverrides: Map<string, boolean | undefined>;
  onToggleTrait: (traitName: string, isSource: boolean) => void;
  activeTraits?: DamageCalculationResult['activeTraits'];
}

export function ActiveTraitsDisplay({
  sourceUnit,
  targetUnit,
  distance,
  motion,
  sourceTraitOverrides,
  targetTraitOverrides,
  onToggleTrait,
  activeTraits
}: ActiveTraitsDisplayProps) {
  // Get all available traits from units
  const sourceTraits = sourceUnit?.specialities?.filter(s => s.startsWith('_')) || [];
  const targetTraits = targetUnit?.specialities?.filter(s => s.startsWith('_')) || [];
  
  if (sourceTraits.length === 0 && targetTraits.length === 0) {
    return null;
  }

  return (
    <Accordion type="single" collapsible className="w-full" defaultValue="unit-traits">
      <AccordionItem value="unit-traits" className="border rounded-lg">
        <AccordionTrigger className="px-3 py-2 hover:no-underline">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Unit Traits</h3>
            <span className="text-xs text-muted-foreground">
              ({sourceTraits.length + targetTraits.length})
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="space-y-3 px-3 pb-3">
            {sourceTraits.length > 0 && (
              <div className="space-y-1.5">
                <h4 className="text-xs font-medium text-muted-foreground">Source</h4>
                <div className="flex flex-wrap gap-1.5">
                  {sourceTraits.map(traitName => {
                    const traitInfo = activeTraits?.source.find(t => t.traitName === traitName);
                    const override = sourceTraitOverrides.get(traitName);
                    
                    return (
                      <TraitToggle
                        key={traitName}
                        traitName={traitName}
                        isActive={traitInfo?.isActive || false}
                        activationType={traitInfo?.activationType || 'unknown'}
                        activationReason={traitInfo?.activationReason}
                        effects={traitInfo?.effects || []}
                        override={override}
                        onToggle={() => onToggleTrait(traitName, true)}
                        isSource={true}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            
            {targetTraits.length > 0 && (
              <div className="space-y-1.5">
                <h4 className="text-xs font-medium text-muted-foreground">Target</h4>
                <div className="flex flex-wrap gap-1.5">
                  {targetTraits.map(traitName => {
                    const traitInfo = activeTraits?.target.find(t => t.traitName === traitName);
                    const override = targetTraitOverrides.get(traitName);
                    
                    return (
                      <TraitToggle
                        key={traitName}
                        traitName={traitName}
                        isActive={traitInfo?.isActive || false}
                        activationType={traitInfo?.activationType || 'unknown'}
                        activationReason={traitInfo?.activationReason}
                        effects={traitInfo?.effects || []}
                        override={override}
                        onToggle={() => onToggleTrait(traitName, false)}
                        isSource={false}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

